/**
 * Progressive Web App (PWA) installation prompt management.
 * Handles beforeinstallprompt and appinstalled events.
 */

'use strict';

import type { DotNetObjectReference, InstallPromptOutcome } from '../types.js';
import { safeInvokeSync } from '../utils/dotnet.js';

/**
 * Internal state for PWA functionality.
 */
interface PwaState {
    dotnet: DotNetObjectReference | null;
    initialized: boolean;
    deferredPrompt: any;
    beforeInstallHandler: ((e: any) => void) | null;
    installedHandler: (() => void) | null;
}

const state: PwaState = {
    dotnet: null,
    initialized: false,
    deferredPrompt: null,
    beforeInstallHandler: null,
    installedHandler: null
};

/**
 * Initialize PWA install prompt listeners.
 * 
 * @param dotnetHelper - Optional .NET object reference for callbacks
 * @returns Whether a deferred install prompt is currently available
 */
export function initialize(dotnetHelper?: DotNetObjectReference): boolean {
    try {
        // Update the current DotNetObjectReference if provided
        if (dotnetHelper) {
            state.dotnet = dotnetHelper;
        }

        // Only attach listeners once
        if (!state.initialized) {
            state.beforeInstallHandler = (e: any) => {
                // Some browsers require preventDefault to allow prompt later
                if (e?.preventDefault) {
                    e.preventDefault();
                }
                state.deferredPrompt = e;
                safeInvokeSync(state.dotnet, 'OnBeforeInstallPromptAvailable');
            };

            state.installedHandler = () => {
                state.deferredPrompt = null;
                safeInvokeSync(state.dotnet, 'OnAppInstalled');
            };

            window.addEventListener('beforeinstallprompt', state.beforeInstallHandler);
            window.addEventListener('appinstalled', state.installedHandler);

            state.initialized = true;
        }

        // Let caller know if the prompt is currently available
        return !!state.deferredPrompt;
    } catch {
        return false;
    }
}

/**
 * Show the native PWA install prompt if available.
 * 
 * @returns Promise resolving to 'accepted' if user accepted, 'dismissed' otherwise
 */
export async function promptInstall(): Promise<InstallPromptOutcome> {
    const promptEvent = state.deferredPrompt;

    if (!promptEvent || typeof promptEvent.prompt !== 'function') {
        return 'dismissed';
    }

    try {
        promptEvent.prompt();
        const choice = await promptEvent.userChoice;
        return choice?.outcome ?? 'dismissed';
    } catch {
        return 'dismissed';
    } finally {
        // Clear the captured prompt per PWA install flow guidance
        state.deferredPrompt = null;
    }
}

/**
 * Dispose PWA install listeners and clear state.
 */
export function dispose(): void {
    try {
        if (state.initialized) {
            if (state.beforeInstallHandler) {
                window.removeEventListener('beforeinstallprompt', state.beforeInstallHandler);
            }
            if (state.installedHandler) {
                window.removeEventListener('appinstalled', state.installedHandler);
            }
        }
    } finally {
        state.deferredPrompt = null;
        state.beforeInstallHandler = null;
        state.installedHandler = null;
        state.dotnet = null;
        state.initialized = false;
    }
}
